import '@rapid-recovery-agency-inc/sloth-ui-web/index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
// TODO: This needs to go away. We need to use the booststrap from the node_modules folder.
import './assets/css/vendor/bootstrap.min.css';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'react-circular-progressbar/dist/styles.css';
import 'react-table/react-table.css';
import 'react-image-lightbox/style.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './assets/css/sass/_insightt.style.scss';
import './assets/css/sass/_insightt.style_2.scss';
// TODO: use css or scss in insightt_styles?
import './assets/css/stylesUtils.css';

import React, { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { oidcEarlyInit } from 'oidc-spa/entrypoint';
import { ENVIRONMENT } from './constants/env-constants';
import { Context } from './contexts';
import { history, store } from './redux/store';
import * as serviceWorker from './serviceWorker';
import { initializeAwsRum } from './shared/monitoring';
import { ErrorBoundaryReload } from './shared/components/ErrorBoundary';
import {
  getAppAuthBoundary,
  getSharedSsoOidcClient,
  hasConfiguredSsoSessionRecord,
  isAuthV2SsoRolloutActive,
  markSsoPathActive,
  planSsoBoot,
  runSsoBoot,
  stripAuthorizationResponseParams,
} from './modules/auth';
import { reportAuthenticationOutcome } from './modules/auth/events';
import { ROUTES } from './constants/app.constants';

/**
 * T022: `oidc-spa`'s required early bootstrap (see `contracts/oidc-provider.md` and
 * research.md §4). This repo does not adopt `oidc-spa`'s Vite plugin (see `vite.config.ts` — no
 * changes were needed there for this task), so this call must happen manually, and as early as
 * possible, per https://docs.oidc-spa.dev — "Call this only if you don't use oidc-spa's Vite
 * plugin." `shouldLoadApp` is `false` only for a hidden, same-origin session-restoration/silent-
 * renewal iframe (research.md §9's shared `prompt=none` episode target): that iframe's only job
 * is to relay the auth response back to the parent window, so it must never boot the full React
 * app, register a service worker, or start monitoring.
 *
 * The boot plan is captured BEFORE `oidcEarlyInit()` runs: it synchronously parses `window.location`
 * and, for a real provider redirect, strips the authorization-response parameters via
 * `history.replaceState` as part of resolving `shouldLoadApp` (see
 * `node_modules/oidc-spa/src/core/earlyInit.ts`'s `handleOidcCallback()`). Boot's callback-rejection
 * classification needs the URL that actually greeted this page load, so it must be read first.
 * `sso-boot.ts`'s `planSsoBoot` holds the decision (gate on, OR a sign-in response, OR an SSO session
 * record; #4193 FR-008, FR-009) so it has unit coverage.
 */
const ssoBootPlan = planSsoBoot({
  search: window.location.search,
  hash: window.location.hash,
  isRolloutActive: isAuthV2SsoRolloutActive,
  hasSsoSessionRecord: hasConfiguredSsoSessionRecord,
}); // Runs whatever the rollout gate says: without an authorization response in the URL it is a no-op,
// and `createOidc()` waits forever for it, so gating it would hang an SSO handoff started after the
// local override is switched on without a reload.
const { shouldLoadApp } = oidcEarlyInit({ BASE_URL: ENVIRONMENT.APP_BASE_URL });

/**
 * Defense-in-depth address-bar sanitization (see `sso-boot.ts`'s `stripAuthorizationResponseParams`
 * doc comment for why `oidcEarlyInit()` above cannot be relied on alone to strip every hostile/
 * malformed authorization-response-shaped URL — it only recognizes a `state` value shaped exactly
 * like one `oidc-spa` itself issues). Runs only when an authorization response was on load
 * ({@link planSsoBoot}), so other pages keep their URL exactly as before (Constitution IX), while a
 * returning sign-in is cleaned up even with the gate off: a no-op when nothing recognized remains
 * (also true once `oidcEarlyInit()` has already rewritten the URL to something clean).
 */
if (ssoBootPlan.shouldSanitizeUrl) {
  const sanitizedSearch = stripAuthorizationResponseParams(window.location.search);
  const sanitizedHash = stripAuthorizationResponseParams(window.location.hash);
  if (sanitizedSearch !== window.location.search || sanitizedHash !== window.location.hash) {
    window.history.replaceState({}, '', `${window.location.pathname}${sanitizedSearch}${sanitizedHash}`);
  }
}

/**
 * REDESIGN (fixes a defect in the originally-shipped T024 `/user/sso/callback` route —
 * `AuthenticationCallback.tsx`/`AUTH_SSO_CALLBACK_PATH`/`AuthenticationSsoCallbackRoute`, all
 * removed): `oidc-spa` never redirects the browser to a dedicated callback route — the OAuth
 * `redirect_uri` it actually uses is always this app's `BASE_URL` (this app's root), hardcoded
 * from `homeUrlAndRedirectUri` with no override parameter at all (see
 * `node_modules/oidc-spa/README.md` line 67 and `node_modules/oidc-spa/core/createOidc.js`). A
 * real login round trip therefore always lands back HERE, at boot, not at any route — so
 * `createSsoOidcClient()` (T022, via `getSharedSsoOidcClient()`) must be invoked unconditionally,
 * once, at app boot, before any component (in particular `AuthRoute.tsx`'s
 * `getAppAuthBoundary().isAuthenticated()` gate) ever renders and reads the boundary.
 *
 * Verified empirically by tracing `createOidc.js` (search `signinRedirectCallback`/
 * `getDesiredPostLoginRedirectUrl`) and `earlyInit.ts`'s `handleOidcCallback()`:
 *
 *  - `oidcEarlyInit()` (just above) is what actually parses the authorization response out of the
 *    URL — not `createOidc()`. For a response matching a real, locally-stored login attempt, it
 *    synchronously (a) strips the response from the visible URL and (b) replaces `window.location`
 *    with the ORIGINAL pre-login `redirectUrl` (`AuthenticationSsoHandoff`'s `login({ redirectUrl
 *    })` call, encoded into the `state` parameter's associated record) via `history.replaceState`
 *    — all before React ever mounts. So by the time this file reaches `runSsoBoot` below, the
 *    visible URL is already the intended post-login destination. The eagerly created router
 *    history still holds the callback URL, so bootstrap synchronizes it before mounting the app.
 *  - `oidcEarlyInit()` stashes the parsed response in its own module-private state for
 *    `createOidc()` to pick up. `createOidc()` (invoked inside `createSsoOidcClient()`) is what
 *    actually performs the code-for-token exchange, reading that stashed response the moment it is
 *    called — this only happens because this file calls `getSharedSsoOidcClient()` here, at boot;
 *    nothing else in this app calls it outside of a route/gateway-decision branch that a plain `/`
 *    landing never mounts.
 *
 * `getSharedSsoOidcClient()` (not `createSsoOidcClient()` directly) is used so this boot-time call
 * and `AuthenticationRouter.tsx`'s `AuthenticationSsoHandoff` (the gateway's later `login()` call)
 * share exactly one client/promise for the page's lifetime — see `oidc-client.ts`'s doc comment.
 *
 * The actual ready/boot_failed/callback_rejected decision is delegated to `sso-boot.ts`'s
 * `runSsoBoot` (kept out of this untested entry point so the decision itself has unit coverage —
 * see that module's doc comment).
 */
async function bootstrap(): Promise<void> {
  const rootElement = document.getElementById('root')!;

  const outcome = await runSsoBoot({
    shouldStartSso: ssoBootPlan.shouldStartSso,
    onSsoStart: markSsoPathActive,
    getSsoOidcClient: getSharedSsoOidcClient,
    getAuthBoundary: getAppAuthBoundary,
    hadAuthorizationResponseOnLoad: ssoBootPlan.hadAuthorizationResponseOnLoad,
    reportOutcome: reportAuthenticationOutcome,
  });

  if (outcome !== 'ready') {
    // A boot-time failure or a rejected callback: never mount the ordinary app (never a partial
    // session, never silently falling through to the gateway/legacy form as if nothing happened —
    // FR-033). Both `AuthenticationRecovery` and `./shared/i18n` are dynamically imported here
    // (rather than imported eagerly above) so this rare path's cost is never paid on an ordinary
    // boot.
    const [{ AuthenticationRecovery }] = await Promise.all([
      import(/* webpackChunkName: "AuthBootRecovery" */ './modules/auth/components/AuthenticationRecovery'),
      import('./shared/i18n.ts'),
    ]);
    createRoot(rootElement).render(
      <StrictMode>
        <AuthenticationRecovery
          variant={outcome === 'callback_rejected' ? 'callback_invalid' : 'unavailable'}
          // A fresh sign-in from the gateway; the consumed callback is never reused (US2/AC2; T107).
          onRetry={() => window.location.assign(ROUTES.auth.login)}
        />
      </StrictMode>,
    );
    return;
  }

  // oidcEarlyInit restores the state-bound destination with native replaceState, which does not
  // notify the history instance created by the eager store import. Otherwise ConnectedRouter
  // mounts at the callback root and SessionRouter redirects to Overview (#4207).
  const { pathname, search, hash } = window.location;
  if (
    history.location.pathname !== pathname ||
    history.location.search !== search ||
    history.location.hash !== hash
  ) {
    history.replace({ pathname, search, hash });
  }

  initializeAwsRum();
  const App = React.lazy(() => import(/* webpackChunkName: "App" */ './App'));

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        console.log('Service Worker registered with scope:', registration.scope);
      })
      .catch((err) => {
        console.log('Service Worker registration failed:', err);
      });
  }

  createRoot(rootElement).render(
    <Context>
      <Provider store={store}>
        <ErrorBoundaryReload>
          <Suspense fallback={<div className="loading" />}>
            {/*https://react.dev/reference/react/StrictMode#enabling-strict-mode-for-entire-app*/}
            <StrictMode>
              <App />
            </StrictMode>
          </Suspense>
        </ErrorBoundaryReload>
      </Provider>
    </Context>,
  );

  /*
   * If you want your app to work offline and load faster, you can change
   * unregister() to register() below. Note this comes with some pitfalls.
   * Learn more about service workers: https://bit.ly/CRA-PWA
   */
  serviceWorker.unregister();
}

if (shouldLoadApp) {
  void bootstrap();
}
